/**
 * `PosDiscountDto` / `CreateOrUpdatePosDiscountBindingModel` from
 * `Gopanza.Api.Services.Abstractions`, plus the shapes of
 * `analytics/reporting/discounts/orders`.
 */

/** `PosDiscountTypes`. The last three take no value — they waive tax. */
export const DISCOUNT_TYPE = {
  fixedAmount: 0,
  percentageAmount: 1,
  noStateTax: 2,
  noMunicipalTax: 3,
  noTaxes: 4,
} as const;

/** Only these two carry a `discountValue`; the rest ignore it. */
export function takesValue(type: number): boolean {
  return (
    type === DISCOUNT_TYPE.fixedAmount || type === DISCOUNT_TYPE.percentageAmount
  );
}

export interface ExcludedItem {
  id: string;
  createdAt: string;
  posDiscountId: string | null;
  itemId: string | null;
  item: {
    id: string;
    gtin: string | null;
    brand: string | null;
    name: string | null;
    itemImage: string | null;
    itemThumbnail: string | null;
  } | null;
}

export interface PosDiscount {
  id: string;
  businessId: string | null;
  createdAt: string;
  nameEn: string | null;
  nameEs: string | null;
  discountType: number;
  discountValue: number;
  canBeAppliedMultipleTimes: boolean;
  isActive: boolean;
  /** Items the discount does **not** apply to; the list endpoint inlines them. */
  excludedItems: ExcludedItem[] | null;
}

/** Exactly what `POST pos/discounts` reads. Anything else sent is ignored. */
export interface PosDiscountInput {
  id: string | null;
  businessId: string | null;
  nameEn: string;
  nameEs: string;
  discountType: number;
  discountValue: number;
  canBeAppliedMultipleTimes: boolean;
  isActive: boolean;
}

export function toInput(discount: PosDiscount): PosDiscountInput {
  return {
    id: discount.id,
    businessId: discount.businessId,
    nameEn: discount.nameEn ?? '',
    nameEs: discount.nameEs ?? '',
    discountType: discount.discountType,
    discountValue: discount.discountValue,
    canBeAppliedMultipleTimes: discount.canBeAppliedMultipleTimes,
    isActive: discount.isActive,
  };
}

export function emptyDiscount(): PosDiscountInput {
  return {
    id: null,
    // Left null, which means every store.
    //
    // The binding model accepts one, but `CreateOrUpdatePosDiscountAsync` only
    // assigns `BusinessId` when the incoming value is non-empty — so a discount
    // scoped to a store can never be widened back to all of them. Offering a
    // store picker here would be offering a one-way door; the list shows the
    // scope of existing rows instead.
    businessId: null,
    nameEn: '',
    nameEs: '',
    discountType: DISCOUNT_TYPE.percentageAmount,
    discountValue: 0,
    canBeAppliedMultipleTimes: false,
    isActive: false,
  };
}

// ------------------------------------------------------------------- report

export interface DiscountUsageOrder {
  id: string;
  confirmationCode: string | null;
  employeeId: string | null;
  employeeName: string | null;
  employeeAvatar: string | null;
  createdAt: string;
  customerId: string | null;
  customerName: string | null;
  customerAvatar: string | null;
  amountApplied: number;
  orderTotal: number;
}

export interface DiscountUsage {
  id: string;
  nameEn: string | null;
  nameEs: string | null;
  discountType: number;
  discountValue: number;
  appliedAmount: number;
  orderTotal: number;
  orderCount: number;
  orders: DiscountUsageOrder[] | null;
}

export interface DiscountsReport {
  total: number;
  totalAmount: number;
  /** Decimal on the wire despite being a count of people. */
  totalCustomers: number;
  discounts: DiscountUsage[] | null;
}
